import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { type TableFeatures } from '@/components/composed/DataTable/table-features';
import type { User, UserRole } from '@/lib/api/domains/users';
import { formatLastLogin } from '@/lib/format';
import { systemUsersQuery } from '../api/user-queries';
import { permissionSetLabel } from '../permission-set-options';

/*
 * The central/admin user list (F02 R37). The operator's own-organization users
 * on the system-configuration surface, answering the same question the
 * entity-record list does — who can get in, and what set they hold. Built for
 * task 8.2 against the task-8.1 tests in CentralUsersList.permission-set.test.tsx:
 *
 *   - AC-6a84fe2f — THE central user list SHALL show the permission set each
 *     account holds: a dedicated "Permission set" column per row.
 *   - AC-c2e4df83 — THE system SHALL show that permission set WITHOUT anyone
 *     having to open the account: the set is on the list row itself, no dialog.
 *   - AC-d621f792 (consumed) — the server (GET /users) scopes the list to the
 *     operator's own organization; the frontend renders EXACTLY those rows and
 *     never widens, filters or reaches beyond them.
 *
 * The list reads `systemUsersQuery` (GET /users). Each row's set comes from
 * `User.permissionSet` on the one shared transport shape, rendered via the
 * `permissionSetLabel` helper, so the central and entity-record lists answer the
 * set the same way (a null set shows a readable "None", never blank).
 */

const ROLE_LABELS: Record<UserRole, string> = {
  tpabilling: 'TPA Billing',
  client: 'Client',
};

export function CentralUsersList(): React.ReactElement {
  // The already-scoped list; the frontend renders exactly these rows and never
  // widens them (AC-d621f792).
  const { data, isLoading, isError } = useQuery(systemUsersQuery());
  const rows = data ?? [];

  // The count on the list summary counts ACTIVE accounts only — a deactivated
  // row is still LISTED, just not counted (AC-aad06fad).
  const activeCount = rows.filter((u) => u.isActive).length;

  const columns: Array<ColumnDef<TableFeatures, User>> = [
    {
      id: 'displayName',
      header: 'Name',
      accessorKey: 'displayName',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.displayName}</span>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.email}</span>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role',
      cell: ({ row }) => <span>{ROLE_LABELS[row.original.role]}</span>,
    },
    {
      // The set the account holds, shown WITHOUT opening the account
      // (AC-6a84fe2f, AC-c2e4df83): a null set (baseline alone) renders a
      // readable "None", never a blank cell. Read via the set-label helper, the
      // same way the entity-record list does.
      id: 'permissionSet',
      header: 'Permission set',
      accessorKey: 'permissionSet',
      cell: ({ row }) => (
        <span>{permissionSetLabel(row.original.permissionSet)}</span>
      ),
    },
    {
      // When the account last signed in (F02 R19/R38): "Never" for a
      // never-signed-in account (AC-fc41f232), else date + time (AC-5608aa1d)
      // in US Eastern for every reader (AC-51bebe6e). Formatted via
      // `formatLastLogin` — no raw Date in JSX (AGENTS.md). The central and
      // entity-record lists answer last login the same way, from the one shared
      // `User.lastLoginOn` shape.
      id: 'lastLoginOn',
      header: 'Last login',
      accessorKey: 'lastLoginOn',
      cell: ({ row }) => (
        <span>{formatLastLogin(row.original.lastLoginOn)}</span>
      ),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      cell: ({ row }) => (
        <StatusBadge
          label={row.original.isActive ? 'Active' : 'Inactive'}
          tone={row.original.isActive ? 'success' : 'neutral'}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* The list summary counts ACTIVE accounts only, excluding deactivated
          ones (AC-aad06fad); the deactivated rows stay on the list below. */}
      <p
        className="text-xs text-muted-foreground"
        data-testid="central-users-active-count"
      >
        {activeCount} active {activeCount === 1 ? 'account' : 'accounts'}.
      </p>
      <DataTable<User>
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        tableId="central-users"
        isLoading={isLoading}
        isError={isError}
        errorMessage="Failed to load users."
        emptyTitle="No users"
        emptyDescription="No user accounts are visible within your scope."
      />
    </div>
  );
}
